import { useQueryClient } from "@tanstack/react-query";
import {
  COUNTRY_GETSELECTED_ERROR,
  COUNTRY_SAVESELECTED_ERROR,
  ERROR_TEXT,
} from "constants/AllStaticText";
import { COUNTRY_SELECTION_STORAGE_NAME } from "constants/country";
import { CONTENT } from "constants/dataLayer";
import { CACHE_KEY } from "constants/general";
import asyncComponentLoader from "core/loader";
import {
  getCookieItem,
  pushToDataLayer,
  setCookieItem,
} from "core/utils/utils.helper";
import { responseMessage } from "helpers/errorHandling.helper";
import useAuth from "hooks/useAuth";
import { useModal } from "hooks/useModal";
import { ModalType } from "models/common";
import {
  createContext,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  getCountrySelection,
  getCountrySelectionList,
  saveCountrySelection,
} from "services/countrySelection.service";
interface CountrySelectContextType {
  country: string | null;
  updateCountry: (country: string) => void;
}

const MODAL_ID = "countrySelectionModal"; // Id for modal, to close modal

// Create context for handling country code selection
export const CountrySelectContext =
  createContext<CountrySelectContextType | null>(null);
// Load CountrySelectionPopup component asynchronously
const CountrySelectionPopup = asyncComponentLoader(() =>
  import("components/CountrySelectionPopup").then(
    ({ CountrySelectionPopup }) => ({
      default: CountrySelectionPopup,
    }),
  ),
);
// CountrySelectProvider component to wrap the components and manage country code selection
export const CountrySelectProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [country, setCountry] = useState<string | null>(null); // For country code
  const isModalVisible = useRef(false); // To prevent multiple modals from appearing
  const isFirstLogInForTheSession = useRef(true); // To limit API call once per logged in session
  const { showModal, closeModal } = useModal(); // Modal methods
  const location = useLocation();
  const path = location.pathname; // For useEffect to trigger on path change
  const auth = useAuth();
  // initialize the quyclien for stale products data cache
  const queryClient: any = useQueryClient();
  const isLoggedIn = auth?.isAuthenticated ?? false;
  // Expiration time (180 days) for data in cookie
  const expiryInMilliseconds = 180 * 24 * 60 * 60 * 1000; // 180 days
  /** Close modal and set ref value to false */
  const handleCloseModal = () => {
    closeModal(MODAL_ID);
    isModalVisible.current = false;
  };

  /**
   * Update country state and cookie when country option is selected from list
   * @param countryCode Country code
   */
  const handleCountrySelection = async (countryCode: string) => {
    // Update country code in API and country state
    await updateCountry(countryCode);
    handleCloseModal(); // Close modal
  };

  /** Get countries list and selected country from API, show modal when required */
  const getCountriesSelectedCountry = async () => {
    // If country state have a value, not user's first login for the session and is logged in, return and prevent function from running
    if (country && !isFirstLogInForTheSession.current && isLoggedIn) {
      return;
    }

    try {
      // Get selected country from API/cookie (if nothing is selected, value will be null) and isDataFromApi to check if the value is coming from a cookie
      const { country: selectedCountry, isDataFromApi } =
        await getSelectedCountry();
      // If selectedCountry is valid, update country state
      if (selectedCountry) {
        if (!isDataFromApi && country !== selectedCountry) {
          // Only call updateCountry if country actually changed (to prevent unnecessary cache invalidation)
          updateCountry(selectedCountry); // Save country value to country save API/cookie according to the logged in state of the user
        } else if (isDataFromApi) {
          setCountry(selectedCountry); // Update state only if the value is from API
          setCookieItem(
            COUNTRY_SELECTION_STORAGE_NAME,
            selectedCountry,
            expiryInMilliseconds,
          );
        }
        isFirstLogInForTheSession.current = false; // Set to false to prevent further API calls during the same session
        return; // Return and prevent function from running
      }

      const countryListResponse = await getCountrySelectionList(); // Get country selection list from API
      if (countryListResponse?.status === 200) {
        const countries =
          countryListResponse &&
          countryListResponse.data &&
          countryListResponse.data.data;
        if (countries?.length > 0 && !isModalVisible.current) {
          // Show modal if countries list is valid and modal not visible
          showModal({
            uId: MODAL_ID, // Modal Id
            children: (
              <CountrySelectionPopup // Modal with countries list
                countriesData={countries}
                onClickFn={handleCountrySelection}
              />
            ),
            type: ModalType.CUSTOM,
            onCancel: handleCloseModal,
            closable: false,
            width: "auto",
          });
          isModalVisible.current = true; // To prevent new modals from appearing
        }
      }
    } catch (e) {
      console.error(e);
      responseMessage(ERROR_TEXT); // Show error message
    }
  };

  /**
   * Get selected country from API/cookie
   * @returns Country code or null if country selection is not done yet, and a boolean to indicate if the value is from cookie
   */
  const getSelectedCountry = async () => {
    // Get cookie since not logged in
    const cookieItem = getCookieItem(COUNTRY_SELECTION_STORAGE_NAME);
    if (isLoggedIn) {
      try {
        const response = await getCountrySelection(); // Get selected country from API
        if (response?.status === 200) {
          const selectedCountry =
            response.data && response.data.data && response.data.data.country;
          const countryCode = selectedCountry || cookieItem;
          return {
            country: countryCode ?? null,
            isDataFromApi: !!selectedCountry,
          }; // Return null value if countryCode is not available
        } else {
          return { country: null, isDataFromApi: false }; // Return null
        }
      } catch (e) {
        responseMessage(COUNTRY_GETSELECTED_ERROR); // Show error message
        return { country: null, isDataFromApi: false }; // Return null
      }
    } else {
      const countryCode = cookieItem;
      // Return the value if present, otherwise null
      return {
        country: countryCode ?? null,
        isDataFromApi: false,
      };
    }
  };

  /**
   * Update country code in API and state if logged in. In logged out state, add/update cookie
   * @param countryCode Country code
   */
  const updateCountry = async (countryCode: string) => {
    // Skip if country hasn't changed to prevent unnecessary cache invalidation
    if (country === countryCode) {
      return;
    }
    pushToDataLayer({
      //country event pushing to dataLayer
      ...CONTENT.PROFILE.COUNTRY_SELECTION,
      user_status: isLoggedIn,
    });
    pushToDataLayer({
      //pushging selected country to datalayer
      country: countryCode,
    });
    // Get cookie value
    const cookieItem = getCookieItem(COUNTRY_SELECTION_STORAGE_NAME);
    // Only invalidate products cache when country actually changes
    queryClient.invalidateQueries([CACHE_KEY.PRODUCTS]);
    if (isLoggedIn) {
      try {
        const payload = { country: countryCode };
        const response = await saveCountrySelection(payload); // Save value to API
        if (response?.status !== 200) {
          responseMessage(COUNTRY_SAVESELECTED_ERROR);
          return;
        }
        setCountry(countryCode); // Update country state if request was succesfull
        // To add cookie if the user has no cookie and modal is being visible to existing logged in users
        // If cookie is not present, add it, to prevent modal from reappearing while users logout
        if (!cookieItem) {
          setCookieItem(
            COUNTRY_SELECTION_STORAGE_NAME,
            countryCode,
            expiryInMilliseconds,
          );
        }
      } catch (e) {
        console.error(e);
        responseMessage(COUNTRY_SAVESELECTED_ERROR); // Show error message
      }
    } else {
      setCountry(countryCode); // Update state
      // Since not logged in, add a cookie with country code value
      // Add/update cookie value only if it is not present or existing value is different, inorder to maintain the expiry intact
      if (!cookieItem || cookieItem !== countryCode) {
        setCookieItem(
          COUNTRY_SELECTION_STORAGE_NAME,
          countryCode,
          expiryInMilliseconds,
        );
      }
    }
  };

  // Trigger function when path changes
  useEffect(() => {
    getCountriesSelectedCountry();
  }, [path]);

  return (
    <CountrySelectContext.Provider value={{ country, updateCountry }}>
      {children}
    </CountrySelectContext.Provider>
  );
};
